import React, { FunctionComponent, useEffect, useState } from 'react'
import { FLEXIBLE_VOLUME_COMPONENT_TYPE } from '@buy/constants/flexible-components'
import { has, isBoolean, isEmpty } from 'lodash-es'
import { DropdownComponent } from '@buy/components/DropdownComponent'
import { PlusMinusComponent } from '@buy/components/PlusMinusComponent'
import { BuyPrice } from '@buy/state/price'
import { OrderSummaryLine } from '@buy/state/order-summary/order-summary'
import { getComponentType } from '@buy/utils/volume-components-helpers'

interface VolumeComponentsWrapperProps {
    skuSelectors: Array<SKUSelectorsResponse>
    closeLabel: string
    baseLine: OrderSummaryLine
    setBaseLine: (value: OrderSummaryLine) => void // change the type
    additionalLines: Array<OrderSummaryLine>
    setAdditionalLines: (value: Array<OrderSummaryLine>) => void
    currencyCode: string
    isYearlyBillingFrequency: boolean
    setVolumeSelectorValues: (unit: string, value: string | number) => void
    volumeSelectorValues: Record<string, string | number>
    billingPeriodKeys: Array<string>
}

export interface SKUSelectorsResponse {
    additionalInformation: string
    description: string
    sku: string
    title: string
    unit: UnitProperties
    pricingInfo: BuyPrice
    planName: string
    volumeSelector?: VolumeSelectorProperties | undefined
    fixedQuantity?: string | undefined
    icon?: string
    displayPriceWithDecimal?: boolean
    includedInSku?: Array<BasePlanSku>
    quantityMultiplier: string
}
export interface BasePlanSku {
    quantity: number
    quantityDisplayLabel: string
    unit: UnitProperties
}
interface UnitProperties {
    id: string
    additionalUnits: string
    unitName: string
    unitNamePlural: string
    updateUnits: string
    usagePriceLabel: string
}

export interface VolumeSelectorProperties {
    maximum?: number
    maximumReachedMessage: string
    minimum?: number
    quantityPerUnit?: number | undefined
    customListValues?: string | undefined
}

const VolumeComponentsWrapper: FunctionComponent<VolumeComponentsWrapperProps> = ({
    skuSelectors,
    closeLabel,
    setAdditionalLines,
    setBaseLine,
    baseLine,
    additionalLines,
    currencyCode,
    isYearlyBillingFrequency,
    volumeSelectorValues,
    setVolumeSelectorValues,
}) => {
    const [showAdditionalLines, setShowAdditionalLines] = useState<boolean | undefined>()

    const getBasePlanSkuDefaultVolume = () => {
        const basePlanSku: SKUSelectorsResponse = skuSelectors.find((item) => has(item, 'includedInSku'))!
        return basePlanSku ? basePlanSku.includedInSku! : []
    }

    const calculateShowAdditionalLinesToggle = () => {
        const basePlanSku: SKUSelectorsResponse = skuSelectors.find((item) => has(item, 'includedInSku'))!
        if (!isEmpty(basePlanSku)) {
            const detectedAdditionalLinesConfig =
                skuSelectors.filter((item) => item.sku === basePlanSku!.sku).length === 1
            setShowAdditionalLines(detectedAdditionalLinesConfig)
        } else setShowAdditionalLines(false)
    }

    useEffect(() => {
        if (!isEmpty(skuSelectors)) {
            calculateShowAdditionalLinesToggle()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [skuSelectors])

    return isBoolean(showAdditionalLines) ? (
        <>
            {skuSelectors?.map((item: SKUSelectorsResponse) => {
                const type = item.volumeSelector && getComponentType(item.volumeSelector)
                if (type === FLEXIBLE_VOLUME_COMPONENT_TYPE.DROPDOWN)
                    return (
                        <DropdownComponent
                            {...item}
                            key={item.unit.id}
                            closeLabel={closeLabel}
                            showAdditionalLines={showAdditionalLines}
                            setAdditionalLines={setAdditionalLines}
                            setBaseLine={setBaseLine}
                            baseLine={baseLine}
                            additionalLines={additionalLines}
                            currencyCode={currencyCode}
                            isYearlyBillingFrequency={isYearlyBillingFrequency}
                            basePlanDefaultVolume={getBasePlanSkuDefaultVolume()}
                            onChange={(value: string) => setVolumeSelectorValues(item.unit.unitName, value)}
                            value={volumeSelectorValues[item.unit.unitName].toString()}
                        />
                    )
                if (type === FLEXIBLE_VOLUME_COMPONENT_TYPE.PLUSMINUS)
                    return (
                        <PlusMinusComponent
                            {...item}
                            key={item.unit.id}
                            setBaseLine={setBaseLine}
                            baseLine={baseLine}
                            showAdditionalLines={showAdditionalLines}
                            isYearlyBillingFrequency={isYearlyBillingFrequency}
                            currencyCode={currencyCode}
                            onChange={(value: number) => setVolumeSelectorValues(item.unit.unitName, value)}
                            value={Number(volumeSelectorValues[item.unit.unitName])}
                        />
                    )
                return null
            })}
        </>
    ) : (
        <></>
    )
}

export default VolumeComponentsWrapper
